// AI-translated content for blog post + widget landing SEO fields.
//
// Complements the hand-translated FAQ + homepage strings in `content_i18n.js`.
// Generated by `frontend/scripts/translate_content.js` (Emergent LLM →
// gpt-4o-mini). Re-run on every content change. English (source) always
// falls back to the raw values defined in `blog.js` / `widget_landings.js`.
//
// v60.16.25 — PERF: Per-locale JSON dictionaries (~25 KB raw each, 11
// locales = ~330 KB raw / ~120 KB gzipped) used to be statically imported
// here, which bundled the ENTIRE multilingual translation corpus into
// every visitor's main.js. The 80%+ of traffic that lands on the English
// site paid that cost for zero benefit. We now lazy-load each locale's
// JSON via dynamic import the moment the visitor's lang flips off "en",
// using `loadLangTranslations(lang)` (called from `I18nProvider`'s
// useEffect). Translations cache for the session so a single round-trip
// covers the entire visit. `tBlog/tWidget/tTool` stay synchronous and
// return the English fallback until the chunk lands — same UX pattern
// already used by `home_i18n_other.js`.

// Dynamic-import factories per locale. Webpack splits each into its own
// chunk + magic-comment chunk name so DevTools shows e.g.
// `i18n.content.es.<hash>.chunk.js` instead of a numeric ID.
const LOADERS = {
  es: () => import(/* webpackChunkName: "i18n.content.es" */ "./i18n/content.es.json"),
  fr: () => import(/* webpackChunkName: "i18n.content.fr" */ "./i18n/content.fr.json"),
  de: () => import(/* webpackChunkName: "i18n.content.de" */ "./i18n/content.de.json"),
  hi: () => import(/* webpackChunkName: "i18n.content.hi" */ "./i18n/content.hi.json"),
  pt: () => import(/* webpackChunkName: "i18n.content.pt" */ "./i18n/content.pt.json"),
  it: () => import(/* webpackChunkName: "i18n.content.it" */ "./i18n/content.it.json"),
  ru: () => import(/* webpackChunkName: "i18n.content.ru" */ "./i18n/content.ru.json"),
  ja: () => import(/* webpackChunkName: "i18n.content.ja" */ "./i18n/content.ja.json"),
  zh: () => import(/* webpackChunkName: "i18n.content.zh" */ "./i18n/content.zh.json"),
  ar: () => import(/* webpackChunkName: "i18n.content.ar" */ "./i18n/content.ar.json"),
  nl: () => import(/* webpackChunkName: "i18n.content.nl" */ "./i18n/content.nl.json"),
};

// In-memory cache of loaded dictionaries. `tBlog/tWidget/tTool` read
// straight from this — populated by `loadLangTranslations(lang)` once
// the dynamic import resolves. Missing entry → English fallback.
const TRANSLATIONS = {};

// Promise dedupe: concurrent calls for the same locale share one fetch.
const INFLIGHT = {};

/**
 * Kick off (and await) the dynamic-import for one locale's translation
 * dictionary. Idempotent — repeat calls for the same locale resolve
 * instantly. Returns the loaded dict (or `null` if the locale doesn't
 * have a JSON, e.g. "en" or unknown codes) so the I18nProvider can
 * bump its re-render version once the dictionary is in cache.
 */
export async function loadLangTranslations(lang) {
  if (!lang || lang === "en") return null;
  if (TRANSLATIONS[lang]) return TRANSLATIONS[lang];
  if (INFLIGHT[lang]) return INFLIGHT[lang];
  const loader = LOADERS[lang];
  if (!loader) return null;
  INFLIGHT[lang] = loader()
    .then((mod) => {
      const dict = mod && (mod.default || mod);
      TRANSLATIONS[lang] = dict || {};
      return TRANSLATIONS[lang];
    })
    .catch(() => {
      // Network blip / parse failure — keep English fallback.
      TRANSLATIONS[lang] = {};
      return TRANSLATIONS[lang];
    })
    .finally(() => {
      delete INFLIGHT[lang];
    });
  return INFLIGHT[lang];
}

/**
 * Translate a single field of a blog post. Falls back to the supplied
 * English value (or empty string) when the lang chunk hasn't loaded
 * yet, the lang has no JSON, or the slug/field isn't present.
 *
 *   tBlog("en", "live-traffic-feed-for-website", "title", englishTitle)  → englishTitle
 *   tBlog("es", "live-traffic-feed-for-website", "title", englishTitle)  → Spanish translation
 */
export function tBlog(lang, slug, field, fallback = "") {
  if (!lang || lang === "en") return fallback;
  const dict = TRANSLATIONS[lang];
  if (!dict) return fallback;
  const post = dict.blog && dict.blog[slug];
  if (!post || !post[field]) return fallback;
  return post[field];
}

/** Translate a single field of a widget landing page. */
export function tWidget(lang, slug, field, fallback = "") {
  if (!lang || lang === "en") return fallback;
  const dict = TRANSLATIONS[lang];
  if (!dict) return fallback;
  const w = dict.widgets && dict.widgets[slug];
  if (!w || !w[field]) return fallback;
  return w[field];
}

/** Translate a single field of an SEO tool entry. Mirrors tWidget — looks
 *  up dict.tools[slug][field], falling back to the EN literal when the
 *  locale dict is missing the entry. Source of truth for translations:
 *  /app/frontend/scripts/inject_seo_tool_titles_i18n.py. */
export function tTool(lang, slug, field, fallback = "") {
  if (!lang || lang === "en") return fallback;
  const dict = TRANSLATIONS[lang];
  if (!dict) return fallback;
  const t = dict.tools && dict.tools[slug];
  if (!t || !t[field]) return fallback;
  return t[field];
}
